
import { defineComponent, ref, watch, PropType } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { ElMessage } from "element-plus"
import type { ElForm } from "element-plus"
import { LotteryCoupon } from "@/store/modules/LotteryCouponModule"

type FormInstance = InstanceType<typeof ElForm>;

export default defineComponent({
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    coupon: {
      type: Object as PropType<LotteryCoupon | null>,
      default: null
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const store = useStore()
    const formRef = ref<FormInstance>()
    const dialogVisible = ref(false)
    const submitting = ref(false)

    const formData = ref({
      grantType: 'mobile',
      mobiles: '',
      user_ids: [] as number[]
    })

    const rules = ref({
      mobiles: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (formData.value.grantType === 'mobile') {
              if (!value || value.trim() === '') {
                callback(new Error('请输入手机号'))
                return
              }
            }
            callback()
          },
          trigger: 'blur'
        }
      ],
      user_ids: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (formData.value.grantType === 'search') {
              if (!value || value.length === 0) {
                callback(new Error('请选择用户'))
                return
              }
            }
            callback()
          },
          trigger: 'change'
        }
      ]
    })

    const userSearchLoading = ref(false)
    const userOptions = ref<Array<{ id: number; nickname: string; mobile: string }>>([])

    watch(() => props.visible, (val) => {
      dialogVisible.value = val
      if (val) {
        // 重置表单
        formData.value = {
          grantType: 'mobile',
          mobiles: '',
          user_ids: []
        }
        userOptions.value = []
      }
    })

    watch(dialogVisible, (val) => {
      emit('update:visible', val)
    })

    const searchUsers = (query: string) => {
      if (query.length < 2) {
        return
      }
      userSearchLoading.value = true
      store
        .dispatch(Actions.GET_MEMBERS, { keyword: query, pageSize: 20 })
        .then(() => {
          userOptions.value = store.getters.currentMembers.map((u: any) => ({
            id: u.id,
            nickname: u.nickname,
            mobile: u.mobile
          }))
          userSearchLoading.value = false
        })
        .catch(() => {
          userSearchLoading.value = false
        })
    }

    const onSubmit = () => {
      if (!formRef.value || !props.coupon) return

      const couponId = props.coupon.id

      formRef.value.validate((valid) => {
        if (valid) {
          if (formData.value.grantType === 'mobile') {
            const mobiles = formData.value.mobiles
              .split('\n')
              .map(m => m.trim())
              .filter(m => m !== '')

            if (mobiles.length === 0) {
              ElMessage.error('请输入有效的手机号')
              return
            }

            submitting.value = true
            store
              .dispatch(Actions.GRANT_LOTTERY_COUPON, {
                id: couponId,
                mobiles: mobiles
              })
              .then((data: any) => {
                submitting.value = false
                if (data.success_count > 0) {
                  ElMessage.success(`成功发放 ${data.success_count} 张优惠券`)
                }
                if (data.not_found && data.not_found.length > 0) {
                  ElMessage.warning(`以下手机号未找到用户: ${data.not_found.join(', ')}`)
                }
                if (data.errors && data.errors.length > 0) {
                  ElMessage.error(`发放失败: ${data.errors.join(', ')}`)
                }
                emit('success')
              })
              .catch(() => {
                submitting.value = false
                ElMessage.error('发放失败')
              })
          } else {
            const user_ids = formData.value.user_ids

            submitting.value = true
            store
              .dispatch(Actions.GRANT_LOTTERY_COUPON, {
                id: couponId,
                user_ids: user_ids
              })
              .then((data: any) => {
                submitting.value = false
                if (data.success_count > 0) {
                  ElMessage.success(`成功发放 ${data.success_count} 张优惠券`)
                }
                if (data.errors && data.errors.length > 0) {
                  ElMessage.error(`发放失败: ${data.errors.join(', ')}`)
                }
                emit('success')
              })
              .catch(() => {
                submitting.value = false
                ElMessage.error('发放失败')
              })
          }
        }
      })
    }

    const onClose = () => {
      dialogVisible.value = false
    }

    return {
      formRef,
      dialogVisible,
      formData,
      rules,
      submitting,
      userSearchLoading,
      userOptions,
      searchUsers,
      onSubmit,
      onClose
    }
  }
})
